import React, {useCallback, useState} from "react";
import {InputComponent} from "../../components/common/input/InputComponent";
import {SearchAddressViewModel} from "../../components/address/SearchAddressViewModel";
import {GetExternalAddressViewModel} from "../../components/address/GetExternalAddressViewModel";
import {RsExternalAddress} from "../../api/dto/RsExternalAddress";
import {ButtonComponent} from "../../components/common/button/ButtonComponent";
import {RsAddress} from "../../api/dto/RsVerification";
import {RqInputAddress} from "../../api/dto/RqInputAddress";
import AutocompleteComponent from "../../components/common/autocomplete/AutocompleteComponent";
import {useParams} from "react-router-dom";
import {RouteParam} from "../../types/types";

export const AddressInformationViewModel =  (onNextClick: () => void) => {

    const { verificationId } = useParams<RouteParam>();
    const { addressCandidates, searchAddresses } = SearchAddressViewModel()
    const { getExternalAddress } = GetExternalAddressViewModel()
    const [street, setStreet] = useState("")
    const [suburb, setSuburb] = useState("")
    const [city, setCity] = useState("")
    const [postalCode, setPostalCode] = useState("")
    const [country, setCountry] = useState("")
    const [addressEditedInformation]
        = useState<RqInputAddress>(RqInputAddress.empty())

    const populateAddressInformation = useCallback((address: RsAddress | undefined) => {
        if (address) {
            setStreet(address.street)
            setSuburb(address.suburb)
            setCity(address.city)
            setPostalCode(address.postalCode)
            setCountry(address.country)
        }
    }, [])

    const markAsEditedAddressInfo = (field: string, func: (value: string) => void, value: string) => {
        switch (field) {
            case "street":
                addressEditedInformation.street = value
                break
            case "suburb":
                addressEditedInformation.suburb = value.toUpperCase()
                break
            case "city":
                addressEditedInformation.city = value
                break
            case "postalCode":
                addressEditedInformation.postCode = value
                break
            case "country":
                addressEditedInformation.country = value
        }
        func(value)
    }

    const onAutocompleteChange = async (searchQuery: string) => {
        if (verificationId) {
            await searchAddresses(searchQuery, verificationId)
        }
    }

    const onAutocompleteSelect = async (selected: string) => {
        const externalId = addressCandidates.find(candidate => candidate.address === selected)?.externalId
        if (externalId && verificationId) {
            const result: RsExternalAddress = await getExternalAddress(externalId, verificationId)
            markAsEditedAddressInfo("street", setStreet, result!!.address1)
            markAsEditedAddressInfo("suburb", setSuburb, result!!.suburb)
            markAsEditedAddressInfo("city", setCity, result!!.city)
            markAsEditedAddressInfo("postalCode", setPostalCode, result!!.postcode)
            markAsEditedAddressInfo("country", setCountry, "New Zealand")
        }
    }

    const isEnabled = !!street && !!suburb && !!city && !!postalCode && !!country

    const buildAddressInfoBlock = (show: boolean) => (
        <div className={`info-position header-back-container ${show ? '' : 'hide-important'}`}>
            <div className={`info-container`}>

                <div className="info-container-title primary-color font-lg full-row">
                    Address
                </div>
                <AutocompleteComponent
                    label="Search"
                    results={addressCandidates.map(candidate => candidate.address)}
                    onChange={(query) => onAutocompleteChange(query)}
                    onSelect={(selected) => onAutocompleteSelect(selected)}
                    maxResults={5}
                    queryDelay={1}
                    containerClass="info-address-title-margin"
                />
                <div className={`info-container-separator width-full`}></div>
                <div className={`font-md primary-color`}>Enter your address manually</div>
                <InputComponent
                    label="Street address"
                    value={street}
                    onChange={(value) => markAsEditedAddressInfo("street", setStreet, value)}/>
                <InputComponent
                    label="Suburb"
                    value={suburb}
                    onChange={(value) => markAsEditedAddressInfo("suburb", setSuburb, value)}/>
                <InputComponent
                    label="City"
                    value={city}
                    onChange={(value) => markAsEditedAddressInfo("city", setCity, value)}/>
                <InputComponent
                    label="Postcode"
                    value={postalCode}
                    onChange={(value) => markAsEditedAddressInfo("postalCode", setPostalCode, value)}
                    digitsOnly
                />
                <InputComponent
                    label="Country"
                    value={country}
                    onChange={(value) => markAsEditedAddressInfo("country", setCountry, value)}
                />

            </div>
            <div className={`width-full info-step-button-container`}>
                <ButtonComponent label="Next" onButtonClick={() => onNextClick()} className={`info-step-button `}
                                 disabled={!isEnabled}/>
            </div>
        </div>
    )

    const addressFieldsAsObject = (): RqInputAddress => {
        return new RqInputAddress(
            street,
            suburb,
            city,
            postalCode,
            country
        )
    }

    return {
        addressFieldsAsObject,
        addressEditedInformation,
        populateAddressInformation,
        buildAddressInfoBlock
    }

}